import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, AlertTriangle, XCircle, CheckCircle2, ShieldCheck, BookOpen, Droplet, ShoppingCart, Utensils, Clock, ChevronRight, Zap, X } from 'lucide-react';
// --- Legal Modal ---
const LegalModal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => <AnimatePresence data-id="element-0">
    {isOpen && <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-1">
        <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} className="bg-charcoal-800 border border-charcoal-700 rounded-sm max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-2">
          <div className="sticky top-0 bg-charcoal-800 border-b border-charcoal-700 px-6 py-4 flex items-center justify-between" data-id="element-3">
            <h3 className="text-xl font-bold uppercase text-cream font-oswald" data-id="element-4">
              {title}
            </h3>
            <button onClick={onClose} className="text-warmgray hover:text-cream transition-colors" data-id="element-5">
              <X className="w-6 h-6" data-id="element-6" />
            </button>
          </div>
          <div className="px-6 py-6 text-warmgray text-sm leading-relaxed space-y-4" data-id="element-7">
            {children}
          </div>
        </motion.div>
      </motion.div>}
  </AnimatePresence>;
// --- Reusable Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay
}} className={className} data-id="element-8">
    {children}
  </motion.div>;
const Divider = () => <div className="w-full h-px bg-charcoal-700 relative flex justify-center items-center my-16" data-id="element-9">
    <div className="absolute w-24 h-px bg-amber-500 shadow-[0_0_10px_rgba(232,114,12,0.8)]" data-id="element-10"></div>
    <Flame className="text-amber-500 w-5 h-5 absolute bg-charcoal-900 px-1" data-id="element-11" />
  </div>;
const CTAButton = ({
  text,
  className = '',
  href
}: {
  text: string;
  className?: string;
  href?: string;
}) => <motion.a href={href || '#offer-section'} target={href ? '_blank' : undefined} rel={href ? 'noopener noreferrer' : undefined} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} onClick={href ? undefined : (e: React.MouseEvent) => {
  e.preventDefault();
  document.getElementById('offer-section')?.scrollIntoView({
    behavior: 'smooth'
  });
}} className={`bg-amber-500 hover:bg-amber-400 text-charcoal-900 font-oswald font-bold text-xl md:text-2xl py-4 px-8 rounded-sm shadow-glow hover:shadow-glow-hover transition-all duration-300 flex items-center justify-center gap-3 uppercase tracking-wider cursor-pointer no-underline ${className}`} data-id="element-12">
    {text}
    <ChevronRight className="w-6 h-6" data-id="element-13" />
  </motion.a>;
// --- Main Page Component ---
export function App() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <div className="min-h-screen bg-charcoal-900 font-source text-cream overflow-x-hidden" data-id="element-14">
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-6 max-w-7xl mx-auto" data-id="element-15">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-16">
          <FadeIn className="order-2 md:order-1" data-id="element-17">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-amber-500/30 bg-amber-500/10 rounded-full text-amber-500 font-bold text-sm mb-6 uppercase tracking-widest" data-id="element-18">
              <Flame className="w-4 h-4" data-id="element-19" />
              For Men 45+
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 uppercase" data-id="element-20">
              <span className="text-amber-500 block" data-id="element-21">Pain-Free</span> Grilling:
              <span className="block text-3xl md:text-4xl lg:text-5xl mt-2 text-cream/90" data-id="element-22">
                The Anti-Inflammatory BBQ Method
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-warmgray mb-10 leading-relaxed" data-id="element-23">
              Reclaim your Sunday ritual and wake up Monday morning without the
              stiffness, gout, or joint flare-ups.
            </p>
            <CTAButton text="Get My Pain-Free Grilling Guide Now" className="w-full md:w-auto" data-id="element-24" />
            <p className="mt-4 text-sm text-warmgray flex items-center gap-2 justify-center md:justify-start" data-id="element-25">
              <ShieldCheck className="w-4 h-4 text-amber-500" data-id="element-26" /> Instant access
              delivered to your inbox.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="order-1 md:order-2 relative" data-id="element-27">
            {/* Subtle background glow behind image */}
            <div className="absolute inset-0 bg-amber-500/10 blur-[100px] rounded-full" data-id="element-28"></div>
            <img src="https://cdn.magicpatterns.com/uploads/rFaVZ5P3i7JTjwN4MEcu8a/Grill_pic.png" alt="Pain-Free Grilling Guide on Tablet and Phone" className="relative z-10 w-full max-w-lg mx-auto drop-shadow-2xl" data-id="element-29" />
          </FadeIn>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="bg-charcoal-800 py-20 px-6 border-y border-charcoal-700" data-id="element-30">
        <div className="max-w-3xl mx-auto text-center" data-id="element-31">
          <FadeIn data-id="element-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-amber-500 uppercase" data-id="element-33">
              You know the feeling.
            </h2>
            <p className="text-xl md:text-2xl text-cream/90 leading-relaxed mb-6" data-id="element-34">
              That Monday morning moment where your knees creak, your lower back
              feels like dry concrete, and your joints remind you exactly what
              you ate at yesterday's BBQ.
            </p>
            <p className="text-lg md:text-xl text-warmgray leading-relaxed" data-id="element-35">
              It's not just "getting older." It's a silent fire burning inside
              your body, fueled by the hidden ingredients you're putting over
              the flames without even knowing it.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-20 px-6 max-w-4xl mx-auto" data-id="element-36">
        <FadeIn data-id="element-37">
          <div className="flex items-center gap-4 mb-8" data-id="element-38">
            <AlertTriangle className="w-10 h-10 text-amber-500 flex-shrink-0" data-id="element-39" />
            <h2 className="text-3xl md:text-4xl font-bold uppercase" data-id="element-40">
              The Real Culprit Isn't The Steak
            </h2>
          </div>
          <div className="prose prose-lg prose-invert max-w-none" data-id="element-41">
            <p className="text-xl text-warmgray mb-6" data-id="element-42">
              For years, you've been told red meat is the enemy and the only
              solution is eating "rabbit food" like lettuce and tofu. You try
              it, but the pain stays.
            </p>
            <div className="bg-charcoal-800 p-8 border-l-4 border-amber-500 rounded-r-sm" data-id="element-43">
              <p className="text-xl font-medium text-cream m-0" data-id="element-44">
                It's the industrial seed oils, the corn-syrup-laden BBQ sauces,
                and the chemical marinades that trigger systemic inflammation in
                men over 45. You are running your most sacred ritual on "dirty
                fuel," and your joints are paying the price.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="bg-charcoal-800 py-20 px-6" data-id="element-45">
        <div className="max-w-4xl mx-auto" data-id="element-46">
          <FadeIn data-id="element-47">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center uppercase" data-id="element-48">
              Why Traditional Advice Fails You
            </h2>
            <div className="space-y-6" data-id="element-49">
              {['Restrictive diets fail because they strip away the pleasure and the protein your body needs to maintain muscle.', "Ibuprofen only masks the symptoms while the internal 'fire' continues to damage your connective tissues.", "Generic health advice doesn't account for the unique biology of the 45+ male and his high sensitivity to inflammatory seed oils."].map((text, i) => <div key={i} className="flex gap-4 items-start bg-charcoal-900 p-6 rounded-sm border border-charcoal-700" data-id="element-50">
                  <XCircle className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" data-id="element-51" />
                  <p className="text-lg md:text-xl text-warmgray" data-id="element-52">{text}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM */}
      <section className="py-24 px-6 max-w-5xl mx-auto text-center" data-id="element-53">
        <FadeIn data-id="element-54">
          <h2 className="text-amber-500 font-bold text-xl tracking-widest uppercase mb-4" data-id="element-55">
            The Solution
          </h2>
          <h3 className="text-4xl md:text-5xl font-bold uppercase mb-8" data-id="element-56">
            The "Grill This, Not That" System
          </h3>
          <p className="text-xl md:text-2xl text-cream/90 leading-relaxed max-w-3xl mx-auto mb-12" data-id="element-57">
            We don't ban the BBQ; we hack it. By using high-smoke-point healthy
            fats, protective marinades that block toxins, and smart carb
            substitutions, we eliminate the inflammatory residue while keeping
            100% of the flavor.
            <br data-id="element-58" />
            <br data-id="element-59" />
            <span className="text-amber-500 font-bold" data-id="element-60">
              It's the science of natural chemistry applied to the pit.
            </span>
          </p>
        </FadeIn>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="bg-charcoal-800 py-20 px-6 border-y border-charcoal-700" data-id="element-61">
        <div className="max-w-6xl mx-auto" data-id="element-62">
          <FadeIn data-id="element-63">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center uppercase" data-id="element-64">
              Inside The Tactical Manual
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-65">
              {[{
              icon: BookOpen,
              title: 'The Master Guide',
              desc: 'Quick-read PDF manual designed for action, not endless reading.'
            }, {
              icon: Droplet,
              title: 'The Oil Code',
              desc: 'Exactly which fats heal your joints and which ones destroy your mobility.'
            }, {
              icon: ShieldCheck,
              title: 'The Shield Marinade',
              desc: 'The exact recipe to block inflammatory compounds before they form on the grill.'
            }, {
              icon: ShoppingCart,
              title: 'Smart Grocery List',
              desc: 'Which cuts of meat to buy and what toxic sauces to leave on the shelf.'
            }, {
              icon: Utensils,
              title: 'Power Sides',
              desc: 'Accompanimientos that keep your insulin (and inflammation) low.'
            }, {
              icon: Clock,
              title: 'The Sunday Master Plan',
              desc: 'A step-by-step timeline from lighting the fire to a pain-free digestion.'
            }].map((item, i) => <div key={i} className="bg-charcoal-900 p-8 rounded-sm border border-charcoal-700 hover:border-amber-500/50 transition-colors" data-id="element-66">
                  <item.icon className="w-10 h-10 text-amber-500 mb-6" data-id="element-67" />
                  <h4 className="text-xl font-bold uppercase mb-3 text-cream" data-id="element-68">
                    {item.title}
                  </h4>
                  <p className="text-warmgray" data-id="element-69">{item.desc}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 px-6 max-w-5xl mx-auto" data-id="element-70">
        <div className="grid md:grid-cols-2 gap-12" data-id="element-71">
          <FadeIn data-id="element-72">
            <h3 className="text-2xl md:text-3xl font-bold uppercase mb-8 flex items-center gap-3 border-b border-charcoal-700 pb-4" data-id="element-73">
              <CheckCircle2 className="text-green-500 w-8 h-8" data-id="element-74" /> Who This Is
              For
            </h3>
            <ul className="space-y-6" data-id="element-75">
              {['Men 45+ who refuse to give up their weekly family BBQ.', 'Men suffering from gout, arthritis, or persistent morning stiffness.', "Those who want to feel athletic and mobile, not 'old and creaky.'", 'Dads and grandfathers who want the energy to play after the meal is over.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-76">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" data-id="element-77" />
                  <span className="text-lg text-warmgray" data-id="element-78">{text}</span>
                </li>)}
            </ul>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-79">
            <h3 className="text-2xl md:text-3xl font-bold uppercase mb-8 flex items-center gap-3 border-b border-charcoal-700 pb-4" data-id="element-80">
              <XCircle className="text-red-500 w-8 h-8" data-id="element-81" /> Who This Is NOT For
            </h3>
            <ul className="space-y-6" data-id="element-82">
              {['People looking for vegan or vegetarian transition guides.', 'Those who would rather take a pill than switch their cooking oil.', 'Anyone unwilling to spend 15 minutes reading a simple operational manual.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-83">
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-84" />
                  <span className="text-lg text-warmgray" data-id="element-85">{text}</span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="bg-charcoal-800 py-20 px-6" data-id="element-86">
        <div className="max-w-4xl mx-auto" data-id="element-87">
          <FadeIn data-id="element-88">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center uppercase" data-id="element-89">
              How It Works
            </h2>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-charcoal-700" data-id="element-90">
              {[{
              step: '1',
              text: "Download the guide and clear your pantry of the 3 main 'Grill Saboteurs.'"
            }, {
              step: '2',
              text: "Apply the 'Shield Marinade' 20 minutes before your next cookout."
            }, {
              step: '3',
              text: 'Wake up Monday feeling light, agile, and ready for the week.'
            }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-91">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-charcoal-800 bg-amber-500 text-charcoal-900 font-bold text-xl shadow-[0_0_0_4px_#1A1A1A] z-10 md:mx-auto shrink-0" data-id="element-92">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-6 bg-charcoal-900 rounded-sm border border-charcoal-700" data-id="element-93">
                    <p className="text-lg md:text-xl text-cream" data-id="element-94">{item.text}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 9. PROOF / BELIEVABILITY */}
      <section className="py-20 px-6 max-w-4xl mx-auto" data-id="element-95">
        <FadeIn data-id="element-96">
          <div className="bg-gradient-to-br from-charcoal-800 to-charcoal-900 p-8 md:p-12 rounded-sm border border-amber-500/30 relative overflow-hidden" data-id="element-97">
            <Zap className="absolute -right-10 -top-10 w-48 h-48 text-amber-500/5" data-id="element-98" />
            <h2 className="text-3xl md:text-4xl font-bold uppercase mb-6 text-amber-500 relative z-10" data-id="element-99">
              The 24-Hour Difference
            </h2>
            <p className="text-xl md:text-2xl text-cream leading-relaxed relative z-10" data-id="element-100">
              By removing seed oils and hidden sugars from your Sunday grill,
              you will notice the inflammation in your hands and knees
              significantly decreases within less than 24 hours. The post-BBQ
              "fog" and lethargy vanish.
            </p>
          </div>
        </FadeIn>
      </section>

      <Divider data-id="element-101" />

      {/* 10. FAQ */}
      <section className="py-10 px-6 max-w-3xl mx-auto" data-id="element-102">
        <FadeIn data-id="element-103">
          <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center uppercase" data-id="element-104">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8" data-id="element-105">
            {[{
            q: 'Do I need to buy expensive specialty meat?',
            a: 'No. We teach you how to pick smart cuts available at any local butcher.'
          }, {
            q: 'Is this a long, complicated cookbook?',
            a: "No. It's a tactical manual for busy men who want results, not recipes."
          }, {
            q: 'Can I still have a drink with my BBQ?',
            a: "Yes. We outline the 'cleanest' options to keep your inflammation levels low."
          }, {
            q: 'How fast can I see results?',
            a: "Usually by the very next morning after your first 'Pain-Free' cookout."
          }].map((faq, i) => <div key={i} className="border-b border-charcoal-700 pb-6" data-id="element-106">
                <h4 className="text-xl font-bold text-cream mb-3 uppercase flex gap-3" data-id="element-107">
                  <span className="text-amber-500" data-id="element-108">Q:</span> {faq.q}
                </h4>
                <p className="text-lg text-warmgray flex gap-3" data-id="element-109">
                  <span className="text-charcoal-700 font-bold" data-id="element-110">A:</span>{' '}
                  {faq.a}
                </p>
              </div>)}
          </div>
        </FadeIn>
      </section>

      {/* 11. OFFER + CTA */}
      <section id="offer-section" className="bg-charcoal-800 py-24 px-6 border-t border-charcoal-700 mt-10" data-id="element-111">
        <div className="max-w-3xl mx-auto text-center" data-id="element-112">
          <FadeIn data-id="element-113">
            <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6" data-id="element-114">
              Reclaim Your Sundays
            </h2>
            <p className="text-xl md:text-2xl text-warmgray mb-10" data-id="element-115">
              Protect your joints for less than the cost of a single low-quality
              ribeye. Get the complete "Pain-Free Grilling" system today.
            </p>

            <div className="bg-charcoal-900 border-2 border-amber-500 p-8 md:p-12 rounded-sm mb-10 inline-block w-full md:w-auto shadow-glow" data-id="element-116">
              <p className="text-warmgray uppercase tracking-widest text-sm font-bold mb-2" data-id="element-117">
                One-Time Investment
              </p>
              <p className="text-warmgray line-through text-2xl font-oswald mb-1" data-id="element-118">
                $12.00
              </p>
              <p className="text-6xl md:text-7xl font-bold text-cream font-oswald" data-id="element-119">
                $6.39
              </p>
              <p className="text-amber-500 font-bold text-sm uppercase tracking-widest mt-2" data-id="element-120">
                47% Off — Limited Time
              </p>
            </div>

            <CTAButton text="Get My Pain-Free Grilling Guide Now" className="w-full md:w-auto mx-auto text-lg md:text-2xl py-5" href="https://guias-salud.com/cart/46136797888700:1" data-id="element-121" />

            <div className="mt-8 flex items-center justify-center gap-6 text-warmgray text-sm" data-id="element-122">
              <span className="flex items-center gap-2" data-id="element-123">
                <ShieldCheck className="w-4 h-4" data-id="element-124" /> Secure Checkout
              </span>
              <span className="flex items-center gap-2" data-id="element-125">
                <Zap className="w-4 h-4" data-id="element-126" /> Instant Access
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-charcoal-900 py-8 px-6 border-t border-charcoal-800" data-id="element-127">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-charcoal-700" data-id="element-128">
          <p data-id="element-129">
            © {new Date().getFullYear()} Pain-Free Grilling. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-130">
            <button onClick={() => setPrivacyOpen(true)} className="hover:text-amber-500 transition-colors underline underline-offset-2" data-id="element-131">
              Privacy Policy
            </button>
            <button onClick={() => setTermsOpen(true)} className="hover:text-amber-500 transition-colors underline underline-offset-2" data-id="element-132">
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-133">
        <p className="text-cream font-semibold" data-id="element-134">
          Effective Date: March 31, 2026
        </p>
        <p data-id="element-135">
          Pain-Free Grilling ("we," "us," or "our") respects your privacy and is
          committed to protecting the personal information you share with us.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-136">
          Information We Collect
        </h4>
        <p data-id="element-137">
          When you purchase our guide, we collect your name and email address
          solely for the purpose of delivering your product and any related
          updates you have opted into.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-138">
          How We Use Your Information
        </h4>
        <p data-id="element-139">
          Your information is used exclusively to deliver the purchased product
          to your inbox, send transactional emails related to your purchase, and
          provide customer support if needed. We do not sell, rent, or share
          your personal information with third parties for marketing purposes.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-140">
          Cookies
        </h4>
        <p data-id="element-141">
          Our website may use cookies to improve your browsing experience and
          analyze site traffic. You can disable cookies through your browser
          settings at any time.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-142">
          Data Security
        </h4>
        <p data-id="element-143">
          We implement industry-standard security measures to protect your
          personal data. However, no method of electronic transmission or
          storage is 100% secure, and we cannot guarantee absolute security.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-144">
          Your Rights
        </h4>
        <p data-id="element-145">
          You may request access to, correction of, or deletion of your personal
          data at any time. To exercise these rights, reach out through the
          channels provided at the time of purchase.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-146">
          Changes to This Policy
        </h4>
        <p data-id="element-147">
          We reserve the right to update this Privacy Policy at any time.
          Changes will be posted on this page with an updated effective date.
        </p>
      </LegalModal>

      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms & Conditions" data-id="element-148">
        <p className="text-cream font-semibold" data-id="element-149">
          Effective Date: March 31, 2026
        </p>
        <p data-id="element-150">
          By purchasing and using the Pain-Free Grilling guide ("the Product"),
          you agree to the following terms and conditions.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-151">
          Product Description
        </h4>
        <p data-id="element-152">
          The Product is a digital informational guide provided in PDF format.
          It contains educational content about grilling techniques and
          ingredient choices. It is not medical advice and should not be used as
          a substitute for professional medical consultation.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-153">
          Disclaimer
        </h4>
        <p data-id="element-154">
          The information provided in this guide is for educational and
          informational purposes only. We make no guarantees regarding specific
          health outcomes. Individual results may vary. Always consult with a
          qualified healthcare professional before making changes to your diet,
          especially if you have existing medical conditions.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-155">
          Payment & Delivery
        </h4>
        <p data-id="element-156">
          Payment is processed securely at the time of purchase. The Product
          will be delivered digitally to the email address you provide. It is
          your responsibility to ensure the email address is correct.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-157">
          Refund Policy
        </h4>
        <p data-id="element-158">
          Due to the digital nature of the Product, all sales are final. If you
          experience a technical issue with delivery, we will work to resolve it
          promptly.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-159">
          Intellectual Property
        </h4>
        <p data-id="element-160">
          All content within the Product is the intellectual property of
          Pain-Free Grilling. You may not reproduce, distribute, or resell any
          part of the Product without prior written consent.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-161">
          Limitation of Liability
        </h4>
        <p data-id="element-162">
          Pain-Free Grilling shall not be held liable for any damages arising
          from the use or inability to use the Product. Use of the information
          is at your own risk.
        </p>
        <h4 className="text-cream font-bold uppercase text-base mt-4" data-id="element-163">
          Changes to These Terms
        </h4>
        <p data-id="element-164">
          We reserve the right to modify these terms at any time. Continued use
          of the Product constitutes acceptance of any changes.
        </p>
      </LegalModal>
    </div>;
}